import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import VerifyTransactionsForm from '../components/forms/VerifyTransactionsForm';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../contexts/AuthContext';
import { STORE_STYLES } from '../assets/data/arrays';
import { ROLE_ID_LIDER_VENTAS } from '../constants/roleIds';

/** Prefijo + color de tienda (misma fuente que OrdersTable / STORE_STYLES). */
const getVerifyTxStoreBadge = (storeName?: string | null) => {
  const name = (storeName ?? '').trim();
  const row = STORE_STYLES.find((s) => s.value === name);
  if (row) {
    return { prefix: row.shortValue, className: row.color };
  }
  const parts = name.split(/\s+/).filter(Boolean);
  let prefix = '??';
  if (parts.length >= 2) {
    prefix = `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  } else if (parts.length === 1) {
    const w = parts[0];
    prefix = w.length >= 2 ? w.slice(0, 2).toUpperCase() : `${(w[0] || '?').toUpperCase()}?`;
  }
  return { prefix, className: 'text-gray-600 font-semibold' };
};

interface Order {
  id: number;
  orders_id: string;
  customer_full_name: string;
  date_created: string;
  store_name: string;
  status: string;
  total: string;
  payment_method?: string;
  transaction_id?: string;
  extraDetails?: {
    payment_method?: string;
    transaction_id?: string;
    pagos?: Array<{
      metodo_pago: string;
      valor: number;
      id_transaccion?: string;
    }>;
  };
}
interface OrderDetail {
  id: number;
  orders_id: number;
  origin_order_id: number;
  store_name: string;
  status: string;
  total: string;
  guia?: string;
  date_created: string;
  date_modified: string;
  agente?: number;
  customer: { first_name: string; last_name: string; email: string; document?: string; tipo_documento?: string;};
  billingAddress: { address_1: string; city: string; state: string; country: string; postcode: string; phone?: string;};
  shippingAddress: { address_1: string; city: string; state: string; country: string; postcode: string; phone?: string;};
  lineItems: Array<{ product_id: string; product_name: string; quantity: number; price: string;}>;
}

/** Normaliza método de pago para comparar (evita fallos por mayúsculas/espacios). */
const normalizePaymentMethod = (value?: string | null) =>
  (value ?? '').toString().trim().toLowerCase()

const isMultiPaymentMethod = (value?: string | null) => {
  const normalized = normalizePaymentMethod(value)
  return normalized === 'part' || normalized === 'plan_separe'
}

/**
 * Pedidos con recaudo contra entrega (COD) no deben listarse aquí:
 * método principal cod, o pagos parciales con algún tramo cod.
 */
const orderInvolvesContraEntrega = (order: Order): boolean => {
  if (normalizePaymentMethod(order.payment_method) === 'cod') {
    return true
  }
  if (normalizePaymentMethod(order.extraDetails?.payment_method) === 'cod') {
    return true
  }
  const pagos = order.extraDetails?.pagos || []
  return pagos.some((p) => normalizePaymentMethod(p.metodo_pago) === 'cod')
}


const VerifyTransactions: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastOrderElementRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, isLoadingMore]);

  const fetchVerifyTransactionOrders = useCallback(async (pageNumber: number) => {
    try {
      setIsLoadingMore(true);
      console.log('🔍 Rol actual del usuario:', user?.role_id);
      console.log('🔍 Usuario completo:', user);
      const token = localStorage.getItem('accessToken');
      // Listado sin filtro por tienda (todas). Restaurar si hace falta limitar de nuevo:
      // &stores=Bigcenter,Distrito Cafetero,Artiq,Garantias
      const response = await axiosInstance.get(
        `/list?&limit=20&page=${pageNumber}&states=verify_transaction`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
       // Filtrar pedidos: excluir contra entrega (cod) y aplicar reglas por rol
       const filteredOrders = response.data.data.filter((order: Order) => {
       const paymentMethod = order.payment_method || order.extraDetails?.payment_method;
        
        console.log(`📦 Pedido ${order.id} - payment_method: ${paymentMethod}, rol: ${user?.role_id}`);

        // Contra entrega (COD): no debe aparecer en esta lista (principal o dentro de pagos parciales)
        if (orderInvolvesContraEntrega(order)) {
          return false;
        }

        // Bold como método único: no listar aquí (sigue en Lista de Pedidos).
        // part / plan_separe con tramos Bold sí se listan para verificar por tramo en el modal.
        if (normalizePaymentMethod(paymentMethod) === 'bold') {
          return false;
        }
        
        // Filtro específico para rol 6 (Líder de Ventas): solo ver bacs, wompi y mpago
        if (user?.role_id === ROLE_ID_LIDER_VENTAS) {
          const metodosPermitidos = ['bacs', 'wompi', 'mpago'];
          
          // Si es pagos parciales, verificar que TODOS los pagos sean permitidos
          if (isMultiPaymentMethod(paymentMethod)) {
            const pagos = order.extraDetails?.pagos || [];
            
            // Histórico incompleto: no bloquear visualización
            if (pagos.length === 0) {
              console.log(`⚠️ Pedido ${order.id} sin pagos definidos (histórico incompleto), se mantiene visible`);
              return true;
            }
            
            // Verificar que TODOS los pagos sean permitidos
            const todosPermitidos = pagos.every(pago => 
              metodosPermitidos.includes(pago.metodo_pago)
            );
            
            if (!todosPermitidos) {
              // Encontrar qué métodos no están permitidos para el log
              const metodosNoPermitidos = pagos
                .filter(p => !metodosPermitidos.includes(p.metodo_pago))
                .map(p => p.metodo_pago);
              console.log(`❌ Filtrando pedido ${order.id} - pagos parciales contienen métodos no permitidos: ${metodosNoPermitidos.join(', ')}`);
              return false;
            }
            
            // Si todos los pagos son permitidos, mostrar
            return true;
          } else {
            // Si NO es pagos parciales, verificar que el método directo sea permitido
            // Si no hay método de pago definido, no mostrar
            if (!paymentMethod || !metodosPermitidos.includes(paymentMethod)) {
              console.log(`❌ Filtrando pedido ${order.id} - método ${paymentMethod || 'no definido'} no permitido para rol 6`);
              return false;
            }
            
            return true;
          }
        }
        
        // Para otros roles, no filtrar
        return true; 
      });

      console.log(`📊 Total pedidos recibidos: ${response.data.data.length}, pedidos filtrados: ${filteredOrders.length}`);
      
      const formattedOrders = filteredOrders.map((order: Order) => ({
        ...order,
        date_created: new Date(order.date_created).toLocaleString(),
      }));

      if (pageNumber === 1) {
        setOrders(formattedOrders);
      } else {
        setOrders(prevOrders => [...prevOrders, ...formattedOrders]);
      }

      setHasMore(formattedOrders.length === 20);
    } catch (error) {
      console.error('Error fetching verify transaction orders:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [user]);

  useEffect(() => {
    // Solo ejecutar si el usuario está cargado
    if (user) {
      fetchVerifyTransactionOrders(page);
    }
  }, [page, fetchVerifyTransactionOrders, user]);

  const handleOrderClick = async (orderId: number) => {
    try {
      const response = await axiosInstance.get(`/list/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const refreshSelectedOrderDetail = useCallback(async () => {
    if (!selectedOrder?.id) return
    try {
      const response = await axiosInstance.get(`/list/${selectedOrder.id}`)
      setSelectedOrder(response.data)
    } catch (error) {
      console.error('Error refreshing order details:', error)
    }
  }, [selectedOrder?.id]);

  return (
    <div className="flex h-full">
      <div className="w-48 bg-white border-r border-gray-200 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <div className="p-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-4">
            lista de Transacciones</h4>
          {isLoading && page === 1 ? (
            <div className="text-center text-gray-500">Cargando pedidos...</div>
          ) : (
            <div className="space-y-2">
              {orders.map((order, index) => {
                const badge = getVerifyTxStoreBadge(order.store_name);
                return (
                <div key={order.id}
                  ref={index === orders.length - 1 ? lastOrderElementRef : null}>
                  <Link to={`/orders/verify-transactions/${order.id}`}
                    onClick={() => handleOrderClick(order.id)}
                    className={`text-sm block p-3 rounded-lg transition-colors ${
                      location.pathname === `/orders/verify-transactions/${order.id}`
                      ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'
                    }`}>
                    <div className="font-semibold">
                      <span className={badge.className}>
                        {' '}{badge.prefix}
                      </span> #{order.id}
                    </div>
                    <div className="text-xs text-gray-500">{order.customer_full_name}</div>
                    <div className="text-xs text-gray-400">{order.date_created}</div>
                  </Link>
                </div>
                );
              })}
              {isLoadingMore && (
                <div className="text-center text-gray-500 py-2">Cargando más pedidos...</div>
              )}
              {!isLoading && orders.length === 0 && (
                <div className="text-sm text-left text-gray-500">No hay pedidos para verificar transacciones</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <Routes>
          <Route path="/" element={<VerifyTransactionsForm />} />
          <Route
            path="/:id"
            element={
              <VerifyTransactionsForm
                orderDetail={selectedOrder}
                onRefreshOrderDetail={refreshSelectedOrderDetail}
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default VerifyTransactions; 